import { useCallback, useEffect, useState, startTransition, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useModalA11y } from '@/shared/lib/hooks';
import { srOnly } from '../../styles/layout.css';
import { ModalCloseButton } from './ModalCloseButton';
import * as s from './Modal.css';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  decoration?: ReactNode;
  footer?: ReactNode;
  /** false로 설정하면 X 버튼, ESC, 오버레이 클릭으로 닫을 수 없음 */
  closable?: boolean;
  /** 모티프 토큰 컨텍스트 (예: "constellation"). portal 루트에 data-motif 부여. */
  motif?: string;
  /** Panel 폭. sm=420 / md=560 (default) / lg=720 / xl=880 */
  size?: ModalSize;
  /** HUD 헤더 라벨 (예: "QUEST", "ATTENDANCE", "CEO"). 안 넘기면 표시 안 됨. */
  tag?: string;
  /**
   * 제목을 헤더에 그리지 않고 접근성 트리에만 남긴다.
   * 본문이 제목 역할을 하는 연출형 모달(수락 완료 등)에서, 시각 디자인을 바꾸지 않으면서
   * dialog 접근명(aria-dialog-name)과 heading 내용(empty-heading)을 채우기 위한 것이다.
   * 이때 본문의 같은 문구는 `aria-hidden` 으로 중복 낭독을 막는다.
   */
  titleHidden?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  decoration,
  footer,
  closable = true,
  motif,
  size = 'md',
  tag,
  titleHidden = false,
}: ModalProps) {
  const titleId = useId();
  const [visible, setVisible] = useState(open);
  const [closing, setClosing] = useState(false);

  const { overlayRef, panelRef, onKeyDown, onOverlayClick } = useModalA11y<HTMLDivElement>(
    onClose,
    {
      visible,
      closing,
      closable,
    }
  );

  useEffect(() => {
    if (open) {
      startTransition(() => {
        setVisible(true);
        setClosing(false);
      });
    } else if (visible) {
      startTransition(() => setClosing(true));
    }
  }, [open, visible]);

  const finishClose = useCallback(() => {
    setVisible(false);
    setClosing(false);
  }, []);

  const handleAnimationEnd = (e: React.AnimationEvent) => {
    if (e.target !== e.currentTarget) return;
    if (closing) finishClose();
  };

  /**
   * 🔴 닫힘 완료를 `animationend` **에만** 걸면 애니메이션이 실행되지 않는 환경에서 모달이 영영
   * 닫히지 않는다 — 오버레이가 화면을 덮은 채 남아 뒤에 있는 창의 닫기 버튼까지 가린다(제보 #125,
   * 병가 입력 후 창 닫기 실패). 두 경우가 실재한다 — (1) `prefers-reduced-motion` 에서
   * panelClosing 의 animation 이 `none` 이라 이벤트가 발화하지 않고, (2) jsdom 은 CSS 애니메이션
   * 자체를 돌리지 않는다. DrawerOverlay 가 같은 이유로 이미 쓰는 안전망 타이머를 여기에도 건다.
   */
  useEffect(() => {
    if (!closing) return;
    const t = setTimeout(finishClose, 400);
    return () => clearTimeout(t);
  }, [closing, finishClose]);

  // 포커스 가둠·되돌리기·ESC·스크롤 잠금은 useModalA11y 가 전담한다 (@/shared/lib).

  if (!visible) return null;

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- 대화상자 안 포커스에서 올라온 Esc·Tab 으로 닫기와 포커스 가둠(useModalA11y)을 처리한다. 배경 클릭은 마우스 편의이고 closable 이면 키보드는 Esc 와 닫기 버튼이 닫는다
    <div
      ref={overlayRef}
      className={`${s.overlay} ${s.overlaySize[size]}${closing ? ` ${s.overlayClosing}` : ''}`}
      onClick={onOverlayClick}
      onKeyDown={onKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-motif={motif}
    >
      {decoration}
      <div
        ref={panelRef}
        className={`${s.panel} ${s.panelSize[size]}${closing ? ` ${s.panelClosing}` : ''}`}
        tabIndex={-1}
        onAnimationEnd={handleAnimationEnd}
      >
        <div className={s.cornerFrame} aria-hidden>
          <span className={s.cornerTL} />
          <span className={s.cornerTR} />
          <span className={s.cornerBL} />
          <span className={s.cornerBR} />
        </div>
        <div className={s.header}>
          <div className={s.titleRow}>
            <span className={s.indicator} aria-hidden>
              ▶
            </span>
            {tag && <span className={s.tag}>{tag}</span>}
            <h2 id={titleId} className={titleHidden ? srOnly : s.title}>
              {title}
            </h2>
          </div>
          {closable && <ModalCloseButton onClose={onClose} />}
        </div>
        <div className={s.body}>{children}</div>
        {footer && <div className={s.footer}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
